export interface BranchProductSlot {
  id: string;
  reference: string;
  brand: string;
  name: string;
  preferredColor: string;
  preferredColorCode: string;
  availableColorCount: number;
  hasLocalImage: boolean;
  description: string;
}

export interface BranchFaq {
  question: string;
  answer: string;
}

export interface BranchLandingPageData {
  slug: string;
  title: string;
  description: string;
  heading: string;
  subheading: string;
  heroText: string;
  heroImage: string;
  heroImageAlt: string;
  secondaryImage: string;
  secondaryImageAlt: string;
  usps: string[];
  introduction: { title: string; paragraphs: string[]; image?: string; imageAlt?: string };
  products: BranchProductSlot[];
  teamSection: { title: string; paragraphs: string[] };
  techniqueSection: { title: string; paragraphs: string[] };
  quoteSection: { title: string; text: string; note: string };
  faqs: BranchFaq[];
}

export const paintersPlasterersPage: BranchLandingPageData = {
  slug: "werkkleding-schilders-stukadoors",
  title: "Werkkleding voor Schilders & Stukadoors Bedrukken | Magic Print",
  description:
    "Werkkleding voor schilders en stukadoors laten bedrukken met je eigen logo. T-shirts, sweaters, werkbroeken, softshells en jassen. Vanaf 10 stuks.",
  heading: "Werkkleding voor schilders & stukadoors",
  subheading: "Professionele werkkleding voor iedere klus",
  heroText:
    "Goede werkkleding moet lekker zitten, tegen een stootje kunnen en prettig werken. Magic Print levert en bedrukt werkkleding voor schilders, stukadoors en afwerkingsbedrijven. Van T-shirts en sweaters tot werkbroeken, softshell jassen en bodywarmers, bedrukt met je eigen logo.",
  heroImage: "header-hero-werkkleding-voor-schilders-stukadoors.webp",
  heroImageAlt: "Schilder en stukadoor in witte werkkleding met verfroller in een te stucen ruimte",
  secondaryImage: "een-professionele-uitstraling-voor-je-hele-team.webp",
  secondaryImageAlt: "Schilders en stukadoors in bedrukte witte werkkleding voor één professionele uitstraling",
  usps: ["Vanaf 10 stuks", "Bedrukt met je eigen logo", "Persoonlijk advies"],
  introduction: {
    title: "Werkkleding die past bij het werk",
    paragraphs: [
      "Als schilder of stukadoor ben je de hele dag in beweging. Je werkt binnen en buiten, op ladders en steigers en regelmatig op je knieën. Dan wil je kleding die comfortabel zit, voldoende bewegingsvrijheid geeft en geschikt is voor dagelijks gebruik.",
      "Daarom hebben we een praktische selectie samengesteld voor verschillende werkzaamheden en seizoenen. Van een licht T-shirt voor warme dagen tot een sweater, werkbroek, softshell of bodywarmer wanneer het kouder wordt.",
    ],
    image: "werkkleding-die-past-bij-het-werk.webp",
    imageAlt: "Schilder in witte werkkleding geknield met verfroller, collega aan het werk op de achtergrond",
  },
  products: [
    {
      id: "wk403-sweater",
      reference: "WK403",
      brand: "WK. Designed To Work",
      name: "Day To Day sweater zakje met contrasterende rits",
      preferredColor: "White / Navy",
      preferredColorCode: "76279",
      availableColorCount: 12,
      hasLocalImage: true,
      description:
        "Comfortabele werksweater voor koelere werkdagen. De witte uitvoering met navy details past uitstekend bij schilders- en stukadoorsbedrijven.",
    },
    {
      id: "k359-longsleeve",
      reference: "K359",
      brand: "Kariban",
      name: "T-shirt ronde hals lange mouwen",
      preferredColor: "White",
      preferredColorCode: "35426",
      availableColorCount: 22,
      hasLocalImage: true,
      description:
        "Praktisch voor binnenwerk en koelere dagen wanneer een T-shirt net te fris is.",
    },
    {
      id: "k356-t-shirt",
      reference: "K356",
      brand: "Kariban",
      name: "T-shirt ronde hals korte mouwen",
      preferredColor: "White",
      preferredColorCode: "35354",
      availableColorCount: 22,
      hasLocalImage: true,
      description:
        "Licht en comfortabel voor warme werkdagen en dagelijks gebruik.",
    },
    {
      id: "uppe145-werkbroek",
      reference: "UPPE145",
      brand: "U-Power",
      name: "Atom herenbroek",
      preferredColor: "White",
      preferredColorCode: "99760",
      availableColorCount: 4,
      hasLocalImage: true,
      description:
        "Stevige werkbroek met voldoende bewegingsvrijheid voor dagelijks gebruik.",
    },
    {
      id: "k401-softshell",
      reference: "K401",
      brand: "Kariban",
      name: "Softshell jas",
      preferredColor: "White",
      preferredColorCode: "34612",
      availableColorCount: 15,
      hasLocalImage: true,
      description:
        "Praktische tussenjas voor buitenwerk in het voor- en najaar.",
    },
    {
      id: "wk607-bodywarmer",
      reference: "WK607",
      brand: "WK. Designed To Work",
      name: "Gewatteerde uniseks bodywarmer van polykatoen multizakken",
      preferredColor: "White",
      preferredColorCode: "76421",
      availableColorCount: 10,
      hasLocalImage: true,
      description:
        "Warme bodywarmer die de armen vrij houdt tijdens het werken en geschikt is voor koelere werkdagen.",
    },
  ],
  teamSection: {
    title: "Eén professionele uitstraling voor je hele team",
    paragraphs: [
      "Of je nu met twee collega's op pad bent of meerdere ploegen hebt rondlopen: herkenbare bedrijfskleding zorgt voor een professionele uitstraling bij klanten.",
      "We kunnen verschillende kledingstukken binnen dezelfde huisstijl bedrukken of borduren. Zo loopt je team er in de zomer én winter herkenbaar bij.",
      "Heb je al een logo? Stuur het mee met je aanvraag. Heb je hulp nodig met het bestand of de plaatsing van het logo? Dan kijken we met je mee.",
    ],
  },
  techniqueSection: {
    title: "Jouw logo op je werkkleding",
    paragraphs: [
      "Werkkleding wordt pas echt onderdeel van je bedrijf met je eigen logo erop. Zo is je team herkenbaar op iedere klus en straal je als bedrijf één geheel uit.",
      "Van een subtiel logo op de borst tot een grotere bedrukking op de rug: we kijken welke plaatsing het beste past bij het kledingstuk en je ontwerp.",
      "Heb je je logo al? Stuur het mee met je aanvraag. Is het bestand niet geschikt voor bedrukking? Dan kijken we met je mee.",
    ],
  },
  quoteSection: {
    title: "Werkkleding nodig voor jouw schilders- of stukadoorsbedrijf?",
    text: "Vertel ons welke kleding je nodig hebt, hoeveel medewerkers je wilt voorzien en stuur eventueel direct je logo mee. We helpen je vervolgens met de kleding, maten, kleuren en beste manier om je logo aan te brengen.",
    note: "Vanaf 10 stuks.",
  },
  faqs: [
    {
      question: "Wat is de minimale afname?",
      answer: "Bij Magic Print kun je bedrijfskleding laten bedrukken vanaf 10 stuks.",
    },
    {
      question: "Kunnen verschillende maten gecombineerd worden?",
      answer: "Ja. Binnen een bestelling kunnen verschillende maten worden opgenomen.",
    },
    {
      question: "Kunnen jullie ons bedrijfslogo op de kleding zetten?",
      answer: "Ja. Afhankelijk van het kledingstuk en logo kunnen we onder andere bedrukken of borduren.",
    },
    {
      question: "Welke kleding is geschikt voor schilders en stukadoors?",
      answer:
        "Dat hangt af van het werk en het seizoen. Denk aan T-shirts, longsleeves en sweaters voor dagelijks gebruik, gecombineerd met een stevige werkbroek. Voor buitenwerk zijn een softshell en warme winterjas goede aanvullingen.",
    },
    {
      question: "Kunnen jullie helpen bij het kiezen van de kleding?",
      answer:
        "Ja. Geef aan waarvoor de kleding gebruikt wordt en wat je ongeveer zoekt. Dan kunnen we adviseren welke kleding en bedrukking daarbij passen.",
    },
  ],
};